'use client';

/* eslint-disable react-refresh/only-export-components */

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  CheckIcon,
  CircleNotchIcon,
  InfoIcon,
  PlusIcon,
  SparkleIcon,
  TagIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { CATEGORY_ICON_NAMES, CategoryIcon } from '@/components/CategoryIcon';
import {
  type CategoryOption,
  type CategoryOptionSource,
  buildPluggyCategoryOptions,
  optionFromCustomCategory,
} from './categoryOptions';
import { ColorSwatchPicker } from './ColorSwatchPicker';
import { IconPicker } from './IconPicker';
import { SuggestionCaption } from './SuggestionCaption';
import { RuleCreationFields, type RuleCreationValue } from './RuleCreationFields';
import { RuleAnchorChoice } from './RuleAnchorChoice';
import { resolveRuleAnchor, type RuleAnchorOption, type RuleSaveOptions } from './ruleAnchor';
import {
  DEFAULT_CATEGORY_ICON,
  applySuggestion,
  categoryVisual,
  defaultCategoryColor,
} from './categoryHelpers';
import { useCategories } from '@/hooks/queries/useCategories';
import { useCustomCategories } from '@/hooks/queries/useCustomCategories';
import { useCreateCustomCategory } from '@/hooks/queries/useCreateCustomCategory';
import { useCategorySuggestion } from '@/hooks/useCategorySuggestion';
import { useSimilarCategorySuggestion } from '@/hooks/useSimilarCategorySuggestion';
import {
  type CategoryIndex,
  getCategoryMeta,
} from '@/components/transactions/screen/categoryIndex';
import { SearchBar } from '@/components/transactions/screen/SearchBar';
import { hexWithAlpha } from '@/lib/colors';
import { useChartSeries } from '@/lib/theme/useThemeColors';
import { cn } from '@/lib/utils';
import type { TransactionCategoryBatchPreview } from '@/lib/api';
import type { CategoryKind, CategorySuggestion, SimilarCategorySuggestion } from '@/types';
import type { CategoryRuleTarget } from '@/types/category-budget';

export type CategorySource = CategoryOptionSource;

/** What's being categorized — deliberately thin; callers own their own domain type. */
export interface CategoryPickerSubject {
  id: string;
  /** Header line: merchant/description. */
  label: string;
  currentCategoryId?: string | null;
  /**
   * Whether `currentCategoryId` is a category the item is ALREADY assigned to — pins and
   * badges it "Atual" at the top of the list. Set to `false` when it's only a suggestion
   * pre-selected for convenience (review-queue items have no real current category; the
   * `user_categorized_before` reason even surfaces its own "Sua escolha anterior" label,
   * which an "Atual" tag on the same row would contradict). Defaults to `true`.
   */
  pinCurrentCategory?: boolean;
}

/** "Sempre categorizar X como Y" checkboxes — review-queue call sites only. */
export interface CategoryPickerRuleCreationConfig {
  /** From `buildRuleAnchors`, best first. Two entries render the anchor selector. */
  anchors: RuleAnchorOption[];
}

/** Batch A — apply to N already-selected review-queue rows (OrcamentoView's multi-select). */
export interface CategoryPickerSelectedIdsBatch {
  ids: string[];
  onSave: (ids: string[], categoryId: string, source: CategorySource) => Promise<void> | void;
}

/** Batch B — apply to every transaction with the same description (TransactionsScreen). */
export interface CategoryPickerSameDescriptionBatch {
  onPreview: (subjectId: string, categoryId: string, source: CategorySource) => Promise<TransactionCategoryBatchPreview>;
  onSave: (
    subjectId: string,
    categoryId: string,
    source: CategorySource,
    ruleAnchor?: CategoryRuleTarget,
  ) => Promise<void>;
  /**
   * This batch ALWAYS mints a rule server-side ("batch ⇒ rule, always"), so the anchor is
   * a decision the user is making whether or not they are shown it — which is why these
   * live here and not in `ruleCreation`: this host has no rule CHECKBOX to hang them off,
   * the batch checkbox is the rule.
   *
   * <p>Absent means this host does not offer the choice at all. A host that resolves them
   * asynchronously reports the gap with <c>anchorsPending</c> instead — "not known yet"
   * and "there is no choice" must never collapse into the same value.</p>
   */
  anchors?: RuleAnchorOption[];

  /**
   * The host is still resolving <c>anchors</c>. Blocks the batch save, because that save
   * mints a rule and would otherwise commit under anchors nobody has seen — and, worse,
   * would send whichever single anchor happened to be derivable meanwhile.
   */
  anchorsPending?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while closed, or while only a selectedIdsBatch is active. */
  subject: CategoryPickerSubject | null;
  /** For the header's "current category" line; omit where the caller has none handy. */
  categoryIndex?: CategoryIndex;
  saving: boolean;
  onSave: (
    subjectId: string,
    categoryId: string,
    source: CategorySource,
    ruleOptions: RuleSaveOptions,
  ) => Promise<void> | void;
  selectedIdsBatch?: CategoryPickerSelectedIdsBatch | null;
  sameDescriptionBatch?: CategoryPickerSameDescriptionBatch;
  ruleCreation?: CategoryPickerRuleCreationConfig;
}

// overflow-CLIP, not hidden: vaul's own `::after` (the bottom-sheet overscroll cover,
// top:100% height:200%) makes the content box scrollable-by-API under overflow-hidden,
// so any browser-initiated scrollIntoView (input focus, mobile keyboard, checkbox
// activation) silently scrolls the sheet's chrome out of view with no touch gesture able
// to bring it back. clip forbids programmatic scrolling entirely; the list scrolls itself.
export const CATEGORY_PICKER_DRAWER_CONTENT_CLASS =
  'flex h-full min-h-0 w-full flex-col overflow-clip px-0 sm:max-w-md data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:h-full data-[vaul-drawer-direction=right]:max-h-none data-[vaul-drawer-direction=right]:w-full';

export type CategoryPickerBatchMode = 'none' | 'selectedIds' | 'sameDescription';

export function canSaveCategorySelection(params: {
  mode: CategoryPickerBatchMode;
  hasSubject: boolean;
  selectedId: string | null;
  currentCategoryId?: string | null;
  /** True when ruleCreation is configured — re-saving the SAME category just to flip
   *  the rule checkbox is still meaningful there, so the unchanged-category guard is
   *  skipped (transactions-style single-purpose recategorize still blocks a no-op save). */
  allowUnchangedCategory: boolean;
  saving: boolean;
  preview: TransactionCategoryBatchPreview | null;
  previewLoading: boolean;
  /** The batch's rule anchors have not resolved yet — see the field's own doc. */
  anchorsPending?: boolean;
}): boolean {
  if (!params.selectedId || params.saving || params.anchorsPending) return false;
  if (params.mode === 'selectedIds') return true;
  if (!params.hasSubject) return false;
  if (params.selectedId === params.currentCategoryId && !params.allowUnchangedCategory) return false;
  if (params.mode !== 'sameDescription') return true;
  if (params.previewLoading || !params.preview) return false;
  return params.preview.updatableCount > 0 && !params.preview.exceedsLimit;
}

export function categorySaveLabel(params: {
  mode: CategoryPickerBatchMode;
  selectedIdsCount: number;
  preview: TransactionCategoryBatchPreview | null;
}): string {
  if (params.mode === 'selectedIds') {
    return `Categorizar ${params.selectedIdsCount} ${params.selectedIdsCount === 1 ? 'lançamento' : 'lançamentos'}`;
  }
  if (params.mode === 'sameDescription' && params.preview && !params.preview.exceedsLimit && params.preview.updatableCount > 0) {
    return `Atualizar ${params.preview.updatableCount} ${params.preview.updatableCount === 1 ? 'lançamento' : 'lançamentos'}`;
  }
  return 'Salvar categoria';
}

/**
 * The one category picker + inline "criar categoria" flow, shared by the Categorias
 * review queue, the Home "Revisar" widget and the Transactions screen's Categorizar
 * pill. Owns its own category data (Pluggy taxonomy + the user's custom categories) and
 * the create-category mutation, so every call site sees the same list and the same
 * "just created" category immediately.
 */
export function CategoryPickerDrawer({
  open,
  onOpenChange,
  subject,
  categoryIndex,
  saving,
  onSave,
  selectedIdsBatch = null,
  sameDescriptionBatch,
  ruleCreation,
}: Props) {
  const { data: parents } = useCategories(open);
  const { data: customCategories = [] } = useCustomCategories(open);
  const { createCategory, creating } = useCreateCustomCategory();

  const isSelectedIdsBatch = !!selectedIdsBatch && selectedIdsBatch.ids.length > 0;
  const batchMode: CategoryPickerBatchMode = isSelectedIdsBatch
    ? 'selectedIds'
    : sameDescriptionBatch
      ? 'sameDescription'
      : 'none';

  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Accepting the similar-category chip or finishing a create both select something the
  // user never saw on screen (they were typing in the create form) and drop them back into
  // list mode — scroll that option into view once it renders, so they don't have to hunt a
  // possibly long list for confirmation that their pick actually landed. Stays pending (not
  // cleared) until the option actually exists — a freshly created category isn't in
  // `options` yet on this render (its SWR cache update is unawaited), so the effect below
  // retries as `filtered` changes rather than giving up after one missed attempt.
  const [pendingScrollToId, setPendingScrollToId] = useState<string | null>(null);
  const optionRefs = useRef(new Map<string, HTMLLIElement>());
  const [createRule, setCreateRule] = useState(false);
  const [applyToMerchant, setApplyToMerchant] = useState(false);
  // Null until the user picks; `resolveRuleAnchor` then falls back to the recommended
  // anchor, so a config that arrives (or changes) after mount never needs a reset effect.
  const [anchorKind, setAnchorKind] = useState<CategoryRuleTarget | null>(null);

  // "apply to every transaction with the same description" batch state.
  const [applyBatch, setApplyBatch] = useState(false);
  const [batchPreview, setBatchPreview] = useState<TransactionCategoryBatchPreview | null>(null);
  const [batchPreviewLoading, setBatchPreviewLoading] = useState(false);
  const [batchPreviewError, setBatchPreviewError] = useState<string | null>(null);

  // Inline "criar categoria" sub-form state.
  const [createName, setCreateName] = useState('');
  const [createIcon, setCreateIcon] = useState<string>(DEFAULT_CATEGORY_ICON);
  const [createColor, setCreateColor] = useState<string>(() => defaultCategoryColor());
  // Categorization is offered for expense, income AND neutral (mov. interna) rows —
  // defaulting to 'expense' only makes sense for the review-queue flow, so every call
  // site gets the same Tipo control NewCategorySheet uses, not a hardcoded kind.
  const [createKind, setCreateKind] = useState<CategoryKind>('expense');
  const [createDirty, setCreateDirty] = useState({ icon: false, color: false, kind: false });
  const [suggested, setSuggested] = useState(false);
  const [flash, setFlash] = useState({ icon: false, color: false, kind: false });
  const flashTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
  }, []);

  // "Did you mean an existing category?" duplicate-avoidance nudge.
  const [similar, setSimilar] = useState<SimilarCategorySuggestion | null>(null);
  const [similarDismissedFor, setSimilarDismissedFor] = useState<string | null>(null);
  const [listVettedName, setListVettedName] = useState('');
  // Link's semantic match for the LIST search term — the substring filter can't connect
  // e.g. "pagamento fatura" to "Pagamento de cartão", so Link fills that gap (see the
  // hook call below for when it runs).
  const [listSimilar, setListSimilar] = useState<SimilarCategorySuggestion | null>(null);
  const [listSimilarDismissedFor, setListSimilarDismissedFor] = useState<string | null>(null);

  const options = useMemo<CategoryOption[]>(
    () => [
      ...customCategories.map((c) => optionFromCustomCategory(c)),
      ...buildPluggyCategoryOptions(parents),
    ],
    [parents, customCategories],
  );
  // Explicitly tracked at every interactive selection (list click, similar-suggestion
  // accept, inline create) so it can never be misresolved to the wrong source — looking
  // it back up in `options` is unreliable right after those actions, since useCustomCategories
  // may still be loading, or the just-created category may not have propagated through the
  // (unawaited) SWR cache update yet. Null only for the untouched bootstrap selection
  // (subject.currentCategoryId on open), which still resolves via `options` below.
  const [selectedSourceOverride, setSelectedSourceOverride] = useState<CategorySource | null>(null);
  const selectedSource: CategorySource =
    selectedSourceOverride ?? options.find((o) => o.id === selectedId)?.source ?? 'pluggy';

  useEffect(() => {
    if (!open) return;
    setSelectedId(isSelectedIdsBatch ? null : (subject?.currentCategoryId ?? null));
    setSelectedSourceOverride(null);
    setQuery('');
    setApplyBatch(false);
    setBatchPreview(null);
    setBatchPreviewError(null);
    setBatchPreviewLoading(false);
    setCreateRule(false);
    setApplyToMerchant(false);
    setAnchorKind(null);
    setMode('list');
    setCreateName('');
    setCreateKind('expense');
    setSimilar(null);
    setSimilarDismissedFor(null);
    setListSimilar(null);
    setListSimilarDismissedFor(null);
    setListVettedName('');
    setPendingScrollToId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject?.id, isSelectedIdsBatch]);

  // Depend on the PRIMITIVE subject fields and the batch's own onPreview reference, not on
  // `subject`/`sameDescriptionBatch` object identity — callers like TransactionsScreen pass
  // fresh wrapper objects on every render (and previewing itself triggers a parent
  // re-render via useFinance's loading state), so depending on the wrappers would cancel
  // and reissue this request on every render instead of once per actual input change.
  const previewSameDescriptionBatch = sameDescriptionBatch?.onPreview;
  const subjectId = subject?.id;
  const subjectCurrentCategoryId = subject?.currentCategoryId;

  useEffect(() => {
    if (
      !open || !applyBatch || !previewSameDescriptionBatch || !subjectId
      || !selectedId || selectedId === subjectCurrentCategoryId
    ) {
      setBatchPreview(null);
      setBatchPreviewError(null);
      setBatchPreviewLoading(false);
      return;
    }

    let alive = true;
    setBatchPreviewLoading(true);
    setBatchPreviewError(null);
    previewSameDescriptionBatch(subjectId, selectedId, selectedSource)
      .then((preview) => {
        if (!alive) return;
        setBatchPreview(preview);
      })
      .catch((err) => {
        if (!alive) return;
        setBatchPreview(null);
        setBatchPreviewError(err instanceof Error ? err.message : 'Não foi possível calcular o lote');
      })
      .finally(() => {
        if (alive) setBatchPreviewLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, applyBatch, previewSameDescriptionBatch, subjectId, subjectCurrentCategoryId, selectedId, selectedSource]);

  // The item's CURRENT category, pinned to the top — stable for the life of the open
  // drawer (derived from the subject prop, not the live selectedId), so it doesn't jump
  // around as the user taps other options. Not applicable to the selectedIds batch mode
  // (no single "current" category across N transactions) or when the subject opts out
  // (pinCurrentCategory: false — currentCategoryId is only a suggestion there, not a fact).
  const pinnedId = isSelectedIdsBatch || subject?.pinCurrentCategory === false
    ? null
    : (subject?.currentCategoryId ?? null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('pt-BR');
    const base = !needle
      ? options
      : options.filter((option) =>
          option.label.toLocaleLowerCase('pt-BR').includes(needle)
          || option.parentLabel?.toLocaleLowerCase('pt-BR').includes(needle),
        );
    if (!pinnedId) return base;
    const pinnedIndex = base.findIndex((option) => option.id === pinnedId);
    if (pinnedIndex <= 0) return base;
    return [base[pinnedIndex], ...base.slice(0, pinnedIndex), ...base.slice(pinnedIndex + 1)];
  }, [options, query, pinnedId]);

  useEffect(() => {
    if (mode !== 'list' || !pendingScrollToId) return;
    const el = optionRefs.current.get(pendingScrollToId);
    if (!el) return; // not rendered yet — re-check once `filtered` updates (e.g. the
    // just-created category's SWR cache catches up), don't give up after one miss.
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setPendingScrollToId(null);
  }, [mode, pendingScrollToId, filtered]);

  const selected = options.find((o) => o.id === selectedId) ?? null;
  const currentCategory = categoryIndex ? getCategoryMeta(categoryIndex, subject?.currentCategoryId) : null;

  // `batchMode` reflects which batch CAPABILITY is configured (a prop, constant for the
  // drawer's lifetime on a given screen); the "same description" one only actually
  // engages once the user ticks the checkbox. canSave/saveLabel must gate on the latter —
  // otherwise a screen that always passes sameDescriptionBatch (Transactions) would
  // permanently require a batch preview even for a plain single-category change.
  // The checkbox itself is a save-time modifier: it renders next to the save button and
  // only once a DIFFERENT category is picked (the preview refuses an unchanged category
  // anyway). Engagement is DERIVED from offerability rather than resetting applyBatch —
  // the checked state survives a detour through the current category and back, and a
  // checked-but-hidden checkbox can never strand the save button disabled.
  const batchOfferable =
    batchMode === 'sameDescription' && !!subject && !!selectedId
    && selectedId !== subject.currentCategoryId;
  const batchEngaged = applyBatch && batchOfferable;
  const effectiveMode: CategoryPickerBatchMode =
    batchMode === 'sameDescription' && !batchEngaged ? 'none' : batchMode;

  // The anchors of whichever control is about to mint a rule: the batch checkbox when it
  // is ticked, the rule checkbox otherwise. Offering a choice that changes nothing would
  // be worse than offering none.
  const anchorOptions: RuleAnchorOption[] = batchEngaged
    ? sameDescriptionBatch?.anchors ?? []
    : ruleCreation?.anchors ?? [];
  const anchor = resolveRuleAnchor(anchorOptions, anchorKind);
  // The selector renders only while a rule is actually in play — the same condition MUST
  // govern what a save submits (see offeredAnchor below): a hidden control is a choice
  // the user never saw.
  const anchorSelectorShown =
    !isSelectedIdsBatch && anchorOptions.length > 1 && (batchEngaged || createRule);
  // Sent ONLY when a choice was actually ON SCREEN. With a single anchor — or with the
  // selector undisclosed because no rule is armed — the user decided nothing, so claiming
  // a decision would override the server's own resolution — and while the host is still
  // resolving them, "a single anchor" means "we don't know yet", which is precisely when
  // overriding it is wrong.
  const offeredAnchor = anchorSelectorShown ? anchor?.kind : undefined;
  // The batch mints a rule, so it must not be submitted while the anchors it would be
  // minted under are still unknown. Costs no perceived wait: this button already waits for
  // the batch preview, which resolves alongside.
  const anchorsPending = batchEngaged && sameDescriptionBatch?.anchorsPending === true;

  const canSave = canSaveCategorySelection({
    mode: effectiveMode,
    hasSubject: !!subject,
    selectedId,
    currentCategoryId: subject?.currentCategoryId,
    allowUnchangedCategory: !!ruleCreation,
    saving,
    preview: batchPreview,
    previewLoading: batchPreviewLoading,
    anchorsPending,
  });
  const saveLabel = categorySaveLabel({
    mode: effectiveMode,
    selectedIdsCount: selectedIdsBatch?.ids.length ?? 0,
    preview: batchPreview,
  });

  const handleSave = async () => {
    if (!selectedId || saving) return;
    if (batchMode === 'selectedIds' && selectedIdsBatch) {
      await selectedIdsBatch.onSave(selectedIdsBatch.ids, selectedId, selectedSource);
      return;
    }
    if (!subject) return;
    if (batchMode === 'sameDescription' && batchEngaged && sameDescriptionBatch) {
      await sameDescriptionBatch.onSave(subject.id, selectedId, selectedSource, offeredAnchor);
      return;
    }
    // Never submit a control that is not currently on screen. `ruleCreation` can vanish
    // under a drawer that stays open on the same subject — the identity is demoted or
    // quarantined and the host's details revalidate — and the reset effect keys on
    // `open`/`subject.id`, so it does not fire. A previously checked box would then be
    // hidden and still sent, creating the very rule the host withdrew the offer for.
    // Derived at submit time rather than mirrored into state: there is no window where
    // the two can disagree.
    const ruleOffered = !!ruleCreation;
    await onSave(subject.id, selectedId, selectedSource, {
      createRule: ruleOffered && createRule,
      applyToMerchant: ruleOffered && applyToMerchant,
      ruleAnchor: offeredAnchor,
    });
  };

  const handleDrawerOpenChange = (next: boolean) => {
    // Blocked while a create is in flight too — handleCreate resolves after this render
    // and writes selectedId/mode; if the drawer had already closed and reopened for a
    // different subject in between, that stale write would select its category there.
    if (!saving && !creating) onOpenChange(next);
  };

  const handleSelectOption = (id: string, source: CategorySource) => {
    setSelectedId(id);
    setSelectedSourceOverride(source);
  };

  const trimmedQuery = query.trim();

  // Only a COMPLETED lookup marks the term as vetted for the create form — a null
  // result included (Link ran and found nothing). An aborted or failed request never
  // does: tapping "Criar categoria" inside the debounce window would otherwise skip
  // both checks and no semantic lookup would ever run for that term. Safe to read the
  // current trimmedQuery here: the hook aborts the inflight request whenever the
  // trimmed term changes, so a response only ever lands for the term still typed.
  const handleListSimilarResult = (s: SimilarCategorySuggestion | null) => {
    setListSimilar(s);
    setListVettedName(trimmedQuery);
  };

  // Link lookup on the search term itself. Only once the substring filter comes up
  // empty — while there are visible matches the list is the answer, and calling the AI
  // on every settled keystroke would be noise. This is also what lets the create form
  // skip re-checking the searched term (listVettedName): Link already vetted it here.
  const { loading: listSimilarLoading } = useSimilarCategorySuggestion({
    enabled: open && mode === 'list' && !saving && filtered.length === 0,
    name: query,
    onSuggestion: handleListSimilarResult,
  });

  // Same stale-signal rule as the create form's rename effect below: a new search term
  // invalidates Link's match for the previous one immediately, not only when the next
  // debounced response settles. Keyed by the TRIMMED term, not the raw input — the hook
  // debounces on name.trim() too, so clearing on a whitespace-only edit would drop a
  // match it will never re-request.
  useEffect(() => {
    setListSimilar(null);
  }, [trimmedQuery]);

  const showListSimilarChip =
    mode === 'list' &&
    !!listSimilar &&
    listSimilarDismissedFor !== trimmedQuery &&
    listSimilar.categoryId !== selectedId &&
    !filtered.some((option) => option.id === listSimilar.categoryId);

  const handleUseListSimilar = () => {
    if (!listSimilar) return;
    setSelectedId(listSimilar.categoryId);
    // The suggestion carries its own source — never re-derive it from `options` (same
    // contract as the create-form chip).
    setSelectedSourceOverride(listSimilar.source);
    setPendingScrollToId(listSimilar.categoryId);
    // Clearing the search is what makes the pick visible — the suggested category is,
    // by construction, not in the current filtered list.
    setQuery('');
  };

  const handleDismissListSimilar = () => setListSimilarDismissedFor(trimmedQuery);

  // ---- inline "criar categoria" flow ----

  const handleCategorySuggestion = (suggestion: CategorySuggestion) => {
    const patch = applySuggestion(suggestion, createDirty, CATEGORY_ICON_NAMES);
    if (patch.icon) setCreateIcon(patch.icon);
    if (patch.color) setCreateColor(patch.color);
    if (patch.kind) setCreateKind(patch.kind);
    if (patch.icon || patch.color || patch.kind) {
      setSuggested(true);
      setFlash({ icon: Boolean(patch.icon), color: Boolean(patch.color), kind: Boolean(patch.kind) });
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(
        () => setFlash({ icon: false, color: false, kind: false }),
        1400,
      );
    }
  };

  const palette = useChartSeries();
  const { loading: suggesting } = useCategorySuggestion({
    enabled: mode === 'create' && !creating,
    name: createName,
    palette,
    onSuggestion: handleCategorySuggestion,
  });
  const suggestionApplied = suggested && !(createDirty.icon && createDirty.color && createDirty.kind);
  // Inset ring (box-shadow, no layout impact, draws inward): a -m/p spacer widens past
  // the scroll container (horizontal scrollbar) and an outward ring gets flat-clipped
  // at the container's left edge.
  const flashRing = (on: boolean) =>
    `rounded-xl transition-shadow duration-700 ${on ? 'ring-2 ring-accent-brand/60 ring-inset' : ''}`;

  // A name edit invalidates every AI-derived signal for the PREVIOUS name — the similar-
  // category match, and any not-yet-touched icon/color/kind suggestion (touched/dirty
  // fields are the user's own explicit picks and must survive a rename). Without this, a
  // fast rename-then-submit before the next debounced suggestion arrives would submit the
  // OLD name's suggested kind under the NEW name — e.g. "Salário" suggests income, the user
  // retypes to "Academia" and creates before the response lands, and it's still income.
  // Depends only on createName by design (see the "reset on open" effect above for the
  // same pattern) — reading the latest createDirty here without listing it keeps this from
  // re-firing (and wrongly clearing `suggested`) on every dirty-flag toggle.
  useEffect(() => {
    setSimilar(null);
    setSuggested(false);
    if (!createDirty.icon) setCreateIcon(DEFAULT_CATEGORY_ICON);
    if (!createDirty.color) setCreateColor(defaultCategoryColor());
    if (!createDirty.kind) setCreateKind('expense');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createName]);

  // The create form is usually reached from the list, where Link already COMPLETED a
  // lookup on this exact search term (listVettedName) — re-validating it here would nag
  // the user twice. Any other name (no search, renamed in the form, or a lookup that
  // was still inflight / failed when create opened) was never vetted, so the check runs.
  const nameAlreadyVetted =
    listVettedName !== '' &&
    createName.trim().toLocaleLowerCase('pt-BR') === listVettedName.toLocaleLowerCase('pt-BR');

  useSimilarCategorySuggestion({
    enabled: mode === 'create' && !creating && !nameAlreadyVetted,
    name: createName,
    onSuggestion: setSimilar,
  });

  const showSimilarChip = mode === 'create' && !!similar && similarDismissedFor !== createName.trim();

  const openCreate = () => {
    setCreateName(query.trim());
    setCreateIcon(DEFAULT_CATEGORY_ICON);
    setCreateColor(defaultCategoryColor());
    setCreateKind('expense');
    setCreateDirty({ icon: false, color: false, kind: false });
    setSuggested(false);
    setSimilar(null);
    setSimilarDismissedFor(null);
    setMode('create');
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name || creating) return;
    const created = await createCategory({ name, icon: createIcon, color: createColor, kind: createKind });
    setSelectedId(created.id);
    // Always 'custom' — never look this back up in `options`, which may not have picked
    // up the (unawaited) SWR cache update from createCategory yet on this render.
    setSelectedSourceOverride('custom');
    setPendingScrollToId(created.id);
    // The category was made for this merchant — the rule holds the future by default.
    if (ruleCreation) setCreateRule(true);
    setQuery('');
    setMode('list');
  };

  const handleCreateNameChange = (e: ChangeEvent<HTMLInputElement>) => setCreateName(e.target.value);

  const handleUseSimilarCategory = () => {
    if (!similar) return;
    setSelectedId(similar.categoryId);
    // The suggestion already carries its source — never re-derive it from `options`,
    // which may still be loading (or missing this exact custom category) right now.
    setSelectedSourceOverride(similar.source);
    setPendingScrollToId(similar.categoryId);
    setMode('list');
    setQuery('');
  };

  const handleDismissSimilar = () => setSimilarDismissedFor(createName.trim());

  const handleSelectKind = (k: CategoryKind) => {
    setCreateKind(k);
    setCreateDirty((d) => ({ ...d, kind: true }));
  };

  const handleColorChange = (hex: string) => {
    setCreateColor(hex);
    setCreateDirty((d) => ({ ...d, color: true }));
  };

  const handleIconChange = (i: string) => {
    setCreateIcon(i);
    setCreateDirty((d) => ({ ...d, icon: true }));
  };

  const handleApplyBatchChange = (e: ChangeEvent<HTMLInputElement>) => setApplyBatch(e.target.checked);

  const handleRuleCreationChange = (next: RuleCreationValue) => {
    setCreateRule(next.createRule);
    setApplyToMerchant(next.applyToMerchant);
  };

  const handleAnchorChange = (kind: CategoryRuleTarget) => {
    setAnchorKind(kind);
    // "Aplicar também aos N anteriores" is hidden at N=0, so an anchor with no prior rows
    // would leave the checkbox armed but invisible — and the save would then ask for a
    // retro-apply the user can no longer see they requested.
    if (resolveRuleAnchor(anchorOptions, kind)?.priorCount === 0) setApplyToMerchant(false);
  };

  const handleBackToList = () => setMode('list');

  return (
    <Drawer open={open} onOpenChange={handleDrawerOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className={cn(CATEGORY_PICKER_DRAWER_CONTENT_CLASS, 'bg-surface')}
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="shrink-0 px-5 py-4 text-left! group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left!">
          <DrawerTitle className="text-lg font-semibold text-text-primary">
            {mode === 'create' ? 'Nova categoria' : 'Categorizar'}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            Escolha uma categoria{mode === 'create' ? ' ou crie uma nova' : ''}.
          </DrawerDescription>
          {mode === 'list' && subject && (
            <div className="mt-2 min-w-0 text-sm/5 text-text-secondary sm:text-xs">
              {/* Merchant labels ("… via iFood") are the whole point of this line — give
                  them the full width on two lines instead of splitting 50/50 with the
                  category, which halved both and hid the platform suffix on mobile. */}
              <div className="flex min-w-0 items-start gap-2">
                <TagIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="duotone" />
                <span className="min-w-0 flex-1 line-clamp-2 wrap-break-word">{subject.label}</span>
              </div>
              {currentCategory && (
                <p className="mt-0.5 truncate pl-6 text-xs text-text-tertiary sm:pl-5.5 sm:text-2xs">
                  {subject.pinCurrentCategory === false ? 'Sugestão' : 'Atual'}: {currentCategory.label}
                </p>
              )}
            </div>
          )}
          {mode === 'list' && isSelectedIdsBatch && (
            <div className="mt-2 text-sm/5 text-text-secondary sm:text-xs">
              {selectedIdsBatch!.ids.length === 1
                ? '1 lançamento selecionado'
                : `${selectedIdsBatch!.ids.length} lançamentos selecionados`}
            </div>
          )}
        </DrawerHeader>

        {mode === 'create' ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-2">
            <div>
              <div className="flex items-center gap-3">
                <span
                  className="grid size-11 shrink-0 place-items-center rounded-xl"
                  style={{ backgroundColor: hexWithAlpha(createColor, 0.12), color: createColor }}
                >
                  <CategoryIcon name={createIcon} className="size-5" />
                </span>
                <div className="flex-1">
                  <label htmlFor="category-picker-create-name" className="mb-1 block text-xs font-semibold text-text-secondary">
                    Nome
                  </label>
                  <input
                    id="category-picker-create-name"
                    autoFocus
                    value={createName}
                    onChange={handleCreateNameChange}
                    placeholder="Cafés & coworking"
                    maxLength={100}
                    className="h-10 w-full rounded-md border border-border-subtle bg-surface px-3 text-base text-text-primary shadow-xs outline-none placeholder:text-text-tertiary focus-visible:border-brand-600 focus-visible:ring-[3px] focus-visible:ring-brand-200/60 sm:text-sm"
                  />
                </div>
              </div>
              <div className="mt-1.5">
                <SuggestionCaption loading={suggesting} applied={suggestionApplied} />
              </div>
            </div>

            {showSimilarChip && (
              <div className="flex items-center gap-2 rounded-lg border border-border-brand/40 bg-brand-50 px-2.5 py-1.5 text-xs text-brand-800">
                <SparkleIcon className="size-3.5 shrink-0 text-accent-brand" weight="fill" />
                <span className="min-w-0 flex-1">
                  Já existe <b>“{similar!.label}”</b>{similar!.parentLabel ? ` em ${similar!.parentLabel}` : ''}.
                </span>
                <button
                  type="button"
                  disabled={creating}
                  className="shrink-0 font-semibold underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleUseSimilarCategory}
                >
                  Usar essa
                </button>
                <button
                  type="button"
                  aria-label="Ignorar sugestão"
                  disabled={creating}
                  className="shrink-0 text-text-tertiary hover:text-text-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleDismissSimilar}
                >
                  <XIcon className="size-3.5" />
                </button>
              </div>
            )}

            <div>
              <p className="mb-1.5 text-xs font-semibold text-text-secondary">Tipo</p>
              <div className={`inline-block ${flashRing(flash.kind)}`}>
                <div className="inline-flex rounded-lg border border-border p-0.5">
                  {(
                    [
                      ['expense', 'Despesa'],
                      ['income', 'Receita'],
                      ['neutral', 'Mov. interna'],
                    ] as const
                  ).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={createKind === k}
                      onClick={() => handleSelectKind(k)}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                        createKind === k ? 'bg-surface text-brand-700 shadow-sm' : 'text-text-secondary'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {createKind === 'neutral' && (
                <p className="mt-1.5 text-2xs text-text-tertiary">
                  Movimentação interna: movimenta dinheiro, mas não conta como gasto — ex.:
                  aportes, transferências, pagamento de fatura.
                </p>
              )}
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold text-text-secondary">Cor</p>
              <div className={flashRing(flash.color)}>
                <ColorSwatchPicker value={createColor} onChange={handleColorChange} />
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold text-text-secondary">Ícone</p>
              <div className={flashRing(flash.icon)}>
                <IconPicker value={createIcon} onChange={handleIconChange} />
              </div>
            </div>

            {ruleCreation && anchor && (
              <p className="text-2xs text-text-tertiary">
                Categoria só sua. Ao salvar, ela já fica selecionada para{' '}
                <b className="text-text-secondary">
                  {anchor.kind === 'merchant' ? anchor.label : `“${anchor.label}”`}
                </b>{' '}
                com a regra marcada.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="shrink-0 border-y border-border px-4 py-3 sm:px-5">
              <SearchBar value={query} onChange={setQuery} placeholder="Buscar categoria" disabled={saving} />
              {/* Same attribution line the create forms show while Link works — without it
                  the list lookup is invisible until the chip pops in. Always mounted: it
                  reserves its own height and is its own live region. */}
              <div className="mt-1.5">
                <SuggestionCaption loading={listSimilarLoading} applied={false} />
              </div>
              {/* Always-mounted live region: the chip arrives async while focus sits in the
                  search input, so it must appear INSIDE an existing polite region to be
                  announced. Empty it costs no height (the chip carries its own mt-2). */}
              <div aria-live="polite">
                {showListSimilarChip && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-border-brand/40 bg-brand-50 px-2.5 py-1.5 text-xs text-brand-800">
                    <SparkleIcon className="size-3.5 shrink-0 text-accent-brand" weight="fill" />
                    <span className="min-w-0 flex-1">
                      Link sugere <b>“{listSimilar!.label}”</b>{listSimilar!.parentLabel ? ` em ${listSimilar!.parentLabel}` : ''}.
                    </span>
                    <button
                      type="button"
                      disabled={saving}
                      className="shrink-0 cursor-pointer font-semibold underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleUseListSimilar}
                    >
                      Usar essa
                    </button>
                    <button
                      type="button"
                      aria-label="Ignorar sugestão"
                      disabled={saving}
                      className="shrink-0 cursor-pointer text-text-tertiary hover:text-text-secondary disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleDismissListSimilar}
                    >
                      <XIcon className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
              <ul className="space-y-0.5">
                {filtered.map((option, index) => {
                  const isSelected = selectedId === option.id;
                  const isPinned = option.id === pinnedId;
                  const visual = categoryVisual(option.id, option.source, option.color);
                  return (
                    <li
                      key={option.id}
                      ref={(el) => {
                        if (el) optionRefs.current.set(option.id, el);
                        else optionRefs.current.delete(option.id);
                      }}
                      className={cn(isPinned && index === 0 && filtered.length > 1 && 'mb-1 border-b border-border-subtle pb-1')}
                    >
                      <button
                        type="button"
                        disabled={saving}
                        aria-pressed={isSelected}
                        onClick={() => handleSelectOption(option.id, option.source)}
                        className={cn(
                          'flex w-full min-w-0 items-center gap-3 rounded-xl p-3 text-left transition-colors sm:py-2.5',
                          isSelected ? 'bg-primary/8' : 'hover:bg-surface-sunken',
                          saving && 'cursor-not-allowed opacity-70',
                        )}
                      >
                        <span
                          className="flex size-10 shrink-0 items-center justify-center rounded-xl sm:size-9"
                          style={{ backgroundColor: visual.backgroundColor }}
                        >
                          <CategoryIcon name={option.iconName} className="size-5 sm:size-4" style={{ color: visual.color }} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="block truncate text-base/6 font-semibold text-text-primary sm:text-sm">
                              {option.label}
                            </span>
                            {isPinned && (
                              <span className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-0.5 text-3xs font-medium text-text-tertiary">
                                Atual
                              </span>
                            )}
                          </span>
                          {option.parentLabel && (
                            <span className="mt-0.5 block truncate text-sm/5 text-text-secondary sm:text-2xs">
                              {option.parentLabel}
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            'flex size-5 shrink-0 items-center justify-center rounded-md border',
                            isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong bg-surface',
                          )}
                          aria-hidden
                        >
                          {isSelected && <CheckIcon className="size-3" weight="bold" />}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <button
                type="button"
                onClick={openCreate}
                disabled={saving}
                className="mt-0.5 flex w-full items-center gap-3 rounded-xl p-3 text-left text-brand-primary transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-dashed border-border sm:size-9">
                  <PlusIcon className="size-4" />
                </span>
                <span className="text-base/6 font-semibold sm:text-sm">
                  Criar categoria{query.trim() ? ` “${query.trim()}”` : ''}
                </span>
              </button>
            </div>

            {/* The commit zone: everything that modifies what Salvar will do. Capped and
                internally scrollable so no combination of batch preview + anchor cards +
                rule fields can ever push the save button below a phone screen — the cap
                only engages in the fully-armed states; the common cases fit outright. */}
            <div className="max-h-[45dvh] min-h-0 shrink-0 overflow-y-auto overscroll-contain">
            {/* Save-time modifier: sits with the save button and appears only once a
                different category is selected — see the batchOfferable derivation. */}
            {batchOfferable && (
              <div className="shrink-0 border-t border-border px-4 py-3 sm:px-5">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-surface-sunken p-3">
                  <span className="group mt-0.5 inline-grid size-5 shrink-0 grid-cols-1 sm:size-4">
                    <input
                      type="checkbox"
                      name="batchSameDescription"
                      checked={applyBatch}
                      disabled={saving}
                      onChange={handleApplyBatchChange}
                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-border-strong bg-surface checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-primary disabled:border-border disabled:bg-surface-sunken disabled:checked:bg-surface-sunken forced-colors:appearance-auto"
                      aria-describedby="batch-same-description-status"
                    />
                    <svg
                      viewBox="0 0 14 14"
                      fill="none"
                      className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white group-not-has-checked:opacity-0"
                      aria-hidden
                    >
                      <path d="M3 8L6 11L11 3.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm/5 font-semibold text-text-primary sm:text-xs/5">
                      Aplicar a lançamentos com a mesma descrição
                    </span>
                    {applyBatch && (
                      <span
                        id="batch-same-description-status"
                        className={cn(
                          'mt-1 flex items-start gap-1.5 text-sm/5 sm:text-xs/5',
                          batchPreviewError || batchPreview?.exceedsLimit
                            ? 'text-warning-text'
                            : 'text-text-secondary',
                        )}
                      >
                        {batchPreviewLoading ? (
                          <>
                            <CircleNotchIcon className="mt-0.5 size-4 shrink-0 animate-spin sm:size-3.5" weight="bold" />
                            Calculando lote...
                          </>
                        ) : batchPreviewError ? (
                          <>
                            <WarningIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                            {batchPreviewError}
                          </>
                        ) : batchPreview?.exceedsLimit ? (
                          <>
                            <WarningIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                            Mais de {batchPreview.limit} lançamentos com essa descrição. Categorize uma parte de cada vez.
                          </>
                        ) : batchPreview ? (
                          <>
                            <InfoIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                            {batchPreview.updatableCount > 0
                              ? `${batchPreview.updatableCount} de ${batchPreview.totalMatches} serão atualizados`
                              : 'Nenhum lançamento novo para atualizar'}
                            {batchPreview.skippedAlreadyCategorizedCount > 0
                              ? `, ${batchPreview.skippedAlreadyCategorizedCount} já estão nessa categoria`
                              : ''}
                          </>
                        ) : (
                          <>
                            <InfoIcon className="mt-0.5 size-4 shrink-0 sm:size-3.5" weight="bold" />
                            Escolha uma categoria diferente para calcular o lote.
                          </>
                        )}
                      </span>
                    )}
                    {/* Suppressed while the anchor selector renders: the selector already
                        says a rule is being minted and for what — and this note's "com
                        essa descrição" would flatly contradict a chosen merchant anchor. */}
                    {applyBatch && anchorOptions.length < 2 && !batchPreviewLoading && !batchPreviewError && batchPreview && !batchPreview.exceedsLimit && (
                      <span className="mt-1 block text-sm/5 text-success-text sm:text-xs/5">
                        Cria uma regra: novos lançamentos com essa descrição entram direto nessa
                        categoria, sem passar pela fila de revisão.
                      </span>
                    )}
                  </span>
                </label>
              </div>
            )}

            {/* Under whichever checkbox mints the rule — it changes what the save is about
                to do. Renders only while a rule is actually in play (the batch is engaged,
                or the "Sempre categorizar" checkbox in the footer below is on) and there
                really are two anchors: before that, the choice would change nothing and
                only push the list off a phone screen. Shares its condition with
                offeredAnchor so a save can never carry an anchor this block never showed. */}
            {anchorSelectorShown && (
              <div className="shrink-0 border-t border-border px-4 py-3 sm:px-5">
                <RuleAnchorChoice
                  options={anchorOptions}
                  value={anchor!.kind}
                  onChange={handleAnchorChange}
                  disabled={saving}
                />
              </div>
            )}

            {!isSelectedIdsBatch && !batchEngaged && ruleCreation && anchor && selected && subject && (
              <div className="shrink-0 border-t border-border-subtle bg-brand-50/50 px-4 py-3 sm:px-5">
                <RuleCreationFields
                  anchor={anchor}
                  categoryName={selected.label}
                  value={{ createRule, applyToMerchant }}
                  onChange={handleRuleCreationChange}
                  disabled={saving}
                  showExplanation
                />
              </div>
            )}
            </div>
          </>
        )}

        <div className="shrink-0 border-t border-border px-4 pt-3 sm:px-5">
          {mode === 'create' ? (
            <div className="flex gap-2 pb-1">
              <button
                type="button"
                onClick={handleBackToList}
                disabled={creating}
                className="h-12 flex-1 rounded-xl border border-border-strong text-base font-semibold text-text-primary disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || createName.trim().length === 0}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
              >
                {creating && <CircleNotchIcon className="size-4 animate-spin" weight="bold" />}
                Criar categoria
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
            >
              {saving && <CircleNotchIcon className="size-4 animate-spin" weight="bold" />}
              {saveLabel}
            </button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
